import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, X, Check, Image as ImageIcon, Search } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL || `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const API_ORIGIN = API.replace(/\/api\/?$/, '');

export default function MediaPickerModal({ isOpen, onClose, onSelect }) {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUrl, setSelectedUrl] = useState('');
  const [error, setError] = useState(null);

  const client = axios.create({
    baseURL: API,
    headers: {
      Authorization: `Bearer ${
        localStorage.getItem('accessToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('access_token')
      }`,
    },
  });

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get('/cms/media', { params: { limit: 100 } });
      const items = (res.data.items || [])
        .filter((item) => !item.content_type || item.content_type.startsWith('image/'))
        .map((item) => /^https?:\/\//i.test(item.url) ? item.url : `${API_ORIGIN}${item.url}`)
        .filter(Boolean);
      setMediaList(Array.from(new Set(items)));
    } catch (err) {
      setError('Could not load media library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadMedia();
    }
  }, [isOpen, loadMedia]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    const body = new FormData();
    body.append('file', file);
    try {
      const { data } = await client.post('/cms/media/upload', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = /^https?:\/\//i.test(data.url) ? data.url : `${API_ORIGIN}${data.url}`;
      setMediaList((prev) => [url, ...prev]);
      setSelectedUrl(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Image upload failed (limit: 5MB for images)');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const filteredMedia = mediaList.filter((url) =>
    url.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs select-none">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#2b3380]" />
            <h2 className="text-base font-bold text-slate-900">Media Library &amp; Upload</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar: Upload Button & Search */}
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
          <label className="cursor-pointer px-4 py-2 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors">
            <Upload className="w-3.5 h-3.5" />
            <span>{uploading ? 'Uploading...' : 'Upload New File'}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              disabled={uploading}
              className="hidden"
            />
          </label>
          <span className="text-[11px] font-medium text-indigo-700">Recommended: at least 1200 px wide · max 5 MB</span>

          <div className="relative flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="w-full bg-white border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2b3380]"
            />
          </div>
        </div>

        {error && (
          <div className="px-4 py-2 bg-red-50 text-red-700 text-xs border-b border-red-100 font-medium">
            {error}
          </div>
        )}

        {/* Grid of Images */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="py-20 text-center text-xs text-slate-400">Loading media files...</div>
          ) : filteredMedia.length === 0 ? (
            <div className="py-20 text-center text-xs text-slate-400">
              No media files found. Upload an image above to get started.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filteredMedia.map((url, idx) => {
                const isSelected = selectedUrl === url;
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedUrl(url)}
                    className={`relative rounded-xl border-2 p-1.5 cursor-pointer bg-slate-50 transition-all flex flex-col items-center justify-center h-28 ${
                      isSelected
                        ? 'border-[#2b3380] bg-indigo-50 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <img
                      src={url}
                      alt=""
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                    {isSelected && (
                      <span className="absolute top-1 right-1 w-5 h-5 bg-[#2b3380] text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                        ✓
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500 truncate max-w-md font-mono">
            {selectedUrl || 'Select an image from the grid'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedUrl}
              onClick={() => {
                onSelect(selectedUrl);
                onClose();
              }}
              className="px-5 py-2 bg-[#2b3380] hover:bg-[#1e2669] disabled:opacity-40 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
            >
              Use Selected Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
